import type { Exercice } from '@mairistem/comptabilite';
import { Icon, type UserRole } from '@mairistem/finances-utils';
import type React from 'react';
import type TreeNodeType from '../../constants/liquidation/TreeNodeType';
import type { TreeRow } from '../../interfaces/pieces/TreeRow';
import type { DemandesRafraichissement } from '../../stores/liquidationTreeviewStore';
import type TreeIndex from './TreeIndex';
import type { TreeIndexLecture } from './TreeIndex';

/** Modifications de l'arbre déclenchées depuis une ligne (actions, modales). */
export interface TreeRowActions {
	/** Met à jour une ligne, sans toucher à ses enfants ; recharge ses enfants déjà chargés si demandé. */
	changeRow: (row: TreeRow, shouldRefreshChildren?: boolean) => void;
	deleteRow: (rowKey: string) => void;
	/** Ajoute une ligne aux enfants chargés de `parentKey`, à sa place dans l'ordre de l'arbre. */
	addRow: (parentKey: string, row: TreeRow) => void;
}

export interface RenderRowOptions {
	filterSens?: string;
	index: TreeIndex;
}

abstract class BaseTreeView {
	/** Enfants d'une ligne : liste vide si elle n'en a pas, null si le chargement a échoué (déjà signalé). */
	abstract loadData(rowData: TreeRow): Promise<TreeRow[] | null>;

	/**
	 * Relit ce que la fiche a modifié et renvoie la correction à appliquer à l'arbre. Elle s'applique à l'index
	 * courant au moment de la réponse, pas à celui de la demande : l'arbre a pu changer pendant le chargement.
	 */
	abstract refreshRows(
		demandes: DemandesRafraichissement,
	): Promise<(index: TreeIndex) => TreeIndex>;

	abstract getRowHeight(rowData: TreeRow, expanded: boolean): number;

	abstract renderRow(
		rowData: TreeRow,
		expanded: boolean,
		userRole: UserRole,
		onClick: VoidFunction,
		onDoubleClick: VoidFunction,
		actions: TreeRowActions,
		options: RenderRowOptions,
	): React.JSX.Element;

	/** Ordre des enfants d'une ligne de type `nodeType`, ou undefined s'ils gardent l'ordre de chargement. */
	abstract compareChildren(
		nodeType: TreeNodeType,
	): ((a: TreeRow, b: TreeRow) => number) | undefined;

	orderData(data: TreeRow[] | null, nodeType: TreeNodeType): TreeRow[] | null {
		const compare = this.compareChildren(nodeType);
		return compare && data ? data.sort(compare) : data;
	}

	/** Exercice de la ligne, ou du premier ancêtre qui en porte un (l'entité). */
	getExercice = (rowData: TreeRow, index?: TreeIndexLecture): Exercice => {
		if (rowData?.exercice) {
			return rowData.exercice;
		}
		return (
			index?.findAncestor(rowData.rowKey, (row) => Boolean(row.exercice))
				?.exercice ?? null
		);
	};

	abstract isRowExpandable(rowData: TreeRow): boolean;

	renderTreeToggle = (
		isExpanded: boolean,
		loading: boolean,
	): React.JSX.Element => (
		<Icon
			color="blue"
			// TODO : revoir
			data-testid="treeToggle-expand-icon"
			size="large"
			loading={loading}
			name={
				loading ? 'circle notch' : `chevron ${isExpanded ? 'down' : 'right'}`
			}
		/>
	);
}

export default BaseTreeView;
